import {List, update} from 'immutable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {AgentNumber} from 'Users/models/User/Agent/AgentNumber';
import {
  changeCanViewAccounts,
  changeCanViewClaims,
  changeCanViewCommissionsAnd1099s,
  changeCanViewCorrespondence,
  changeCanViewPolicies,
  changeCanViewPolicyServiceRequest,
  EmptySupportAgencyAssociation,
  SupportAgencyAssociation,
  ValidSupportAgencyAssociation,
  loadTaxIdentificationNumber,
  changeTaxIdentificationNumber,
  changeCanViewPendingUnderwritingRequirements,
  addAgentNumber,
  removeAgentNumber,
} from 'Users/models/User/Agent/SupportAgencyAssociation';
import {
  TaxIdentificationNumberWithNoAgentNumbers,
  ValidTaxIdentificationNumber,
} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {AgentRole} from 'Users/models/User/AgentUserType';

export class UnvalidatedAgentSupportUserType {
  public readonly key = 'UnvalidatedAgentSupportUserType';
  public readonly agentRole = AgentRole.AgentSupport;

  constructor(public readonly agencyAssociations: List<SupportAgencyAssociation>) {}
}

export class InvalidAgentSupportUserType {
  public readonly key = 'InvalidAgentSupportUserType';
  public readonly agentRole = AgentRole.AgentSupport;

  constructor(public readonly agencyAssociations: List<SupportAgencyAssociation>) {}
}

const isEveryAgencyAssociationValid = (
  agencyAssociation: List<SupportAgencyAssociation>
): agencyAssociation is List<ValidSupportAgencyAssociation> =>
  agencyAssociation.every(aa => aa instanceof ValidSupportAgencyAssociation);

export class ValidAgentSupportUserType {
  public static create(agencyAssociations: List<SupportAgencyAssociation>) {
    const hasDuplicateTaxId = agencyAssociations.map(aa => aa.taxId.value).toSet().size !== agencyAssociations.size;

    return agencyAssociations.count() > 0 && isEveryAgencyAssociationValid(agencyAssociations) && !hasDuplicateTaxId
      ? new ValidAgentSupportUserType(agencyAssociations)
      : new InvalidAgentSupportUserType(agencyAssociations);
  }

  public readonly key = 'ValidAgentSupportUserType';
  public readonly agentRole = AgentRole.AgentSupport;

  private constructor(public readonly agencyAssociations: List<ValidSupportAgencyAssociation>) {}
}

export const addAgencyAssociation = ({agencyAssociations}: AgentSupportUserType) => {
  const associations: List<SupportAgencyAssociation> = agencyAssociations;

  return ValidAgentSupportUserType.create(
    associations
      .push(EmptySupportAgencyAssociation)
      .map(({taxId, supportAgentPermissions, agentNumbers}) =>
        ValidSupportAgencyAssociation.create(taxId, supportAgentPermissions, agentNumbers)
      )
  );
};

export const removeAgencyAssociation = (index: number) => ({agencyAssociations}: AgentSupportUserType) => {
  const associations: List<SupportAgencyAssociation> = agencyAssociations;

  return ValidAgentSupportUserType.create(
    associations
      .remove(index)
      .map(({taxId, supportAgentPermissions, agentNumbers}) =>
        ValidSupportAgencyAssociation.create(taxId, supportAgentPermissions, agentNumbers)
      )
  );
};

export const loadTaxIdNumber = (
  index: number,
  taxId: ValidTaxIdentificationNumber | TaxIdentificationNumberWithNoAgentNumbers
) => ({agencyAssociations}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(update(agencyAssociations as any, index, loadTaxIdentificationNumber(taxId)));

export const changeAgencyAssociationTaxId = (index: number, taxId: string) => ({
  agencyAssociations,
}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(update(agencyAssociations as any, index, changeTaxIdentificationNumber(taxId)));

export const addAgentNumberToAgencyAssociation = (index: number, agentNumber: AgentNumber) => ({
  agencyAssociations,
}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(update(agencyAssociations as any, index, addAgentNumber(agentNumber)));

export const removeAgentNumberFromAgencyAssociation = (index: number, agentNumber: AgentNumber) => ({
  agencyAssociations,
}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(update(agencyAssociations as any, index, removeAgentNumber(agentNumber)));

export const changeAgencyAssociationCanViewAccounts = (index: number, canViewAccounts: boolean) => ({
  agencyAssociations,
}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(update(agencyAssociations as any, index, changeCanViewAccounts(canViewAccounts)));

export const changeAgencyAssociationCanViewPolicies = (index: number, canViewPolicies: boolean) => ({
  agencyAssociations,
}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(update(agencyAssociations as any, index, changeCanViewPolicies(canViewPolicies)));

export const changeAgencyAssociationCanViewClaims = (index: number, canViewClaims: boolean) => ({
  agencyAssociations,
}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(update(agencyAssociations as any, index, changeCanViewClaims(canViewClaims)));

export const changeAgencyAssociationCanViewCommissionsAnd1099s = (
  index: number,
  canViewCommissionsAnd1099s: boolean
) => ({agencyAssociations}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(
    update(agencyAssociations as any, index, changeCanViewCommissionsAnd1099s(canViewCommissionsAnd1099s))
  );

export const changeAgencyAssociationCanViewPolicyServiceRequest = (
  index: number,
  canViewPolicyServiceRequest: boolean
) => ({agencyAssociations}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(
    update(agencyAssociations as any, index, changeCanViewPolicyServiceRequest(canViewPolicyServiceRequest))
  );

export const changeAgencyAssociationCanViewCorrespondence = (index: number, canViewCorrespondence: boolean) => ({
  agencyAssociations,
}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(
    update(agencyAssociations as any, index, changeCanViewCorrespondence(canViewCorrespondence))
  );

export const changeAgencyAssociationCanViewPendingUnderwritingRequirements = (
  index: number,
  canViewPendingUnderwritingRequirements: boolean
) => ({agencyAssociations}: AgentSupportUserType) =>
  ValidAgentSupportUserType.create(
    update(
      agencyAssociations as any,
      index,
      changeCanViewPendingUnderwritingRequirements(canViewPendingUnderwritingRequirements)
    )
  );

export type AgentSupportUserType =
  | ValidAgentSupportUserType
  | InvalidAgentSupportUserType
  | UnvalidatedAgentSupportUserType;

export type AgentSupportUserTypeOptions<TReturn> = ToMatchOptions<AgentSupportUserType, 'key', TReturn>;

export const matchAgentSupportUserType = <TReturn>(
  agentUserType: AgentSupportUserType,
  options: AgentSupportUserTypeOptions<TReturn>
): TReturn => {
  switch (agentUserType.key) {
    case 'InvalidAgentSupportUserType':
      return options.InvalidAgentSupportUserType(agentUserType);
    case 'UnvalidatedAgentSupportUserType':
      return options.UnvalidatedAgentSupportUserType(agentUserType);
    case 'ValidAgentSupportUserType':
      return options.ValidAgentSupportUserType(agentUserType);
  }
};

export const EmptyAgentSupportUserType = new UnvalidatedAgentSupportUserType(List([EmptySupportAgencyAssociation]));
